import React, {
  useMemo, useCallback, useState, useEffect,
} from 'react';
import {
  Col, Divider, Row, Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DollarCircleOutlined, SettingOutlined } from '@ant-design/icons';

import { getType, updateData } from './workflowHelpers';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import HoverHelp from '../../common/HoverHelp';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import useToggle from '../../common/hooks/useToggle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import {
  EQUIPMENT_CATEGORY_ID,
  ID_TO_CATEGORY_MAP,
  LABOR_CATEGORY_ID,
} from '../../helpers/costcodeHelpers';

const getHoverText = (t) => `This step can update a ${t('Project').toLowerCase()}'s cost based on the fields submitted. Costs from a Time Entry Table go to each time entry's own ${t('Project').toLowerCase()}, and Time Tracking Grid equipment costs go to each column's ${t('Project').toLowerCase()}.`;
const getDisabledText = (t) => `Add a Time Entry Table, a Time Tracking Grid, or a ${t('Project')} Dropdown and one of Material Table, Equipment Table, Costcode Table or Sub-Contract Dropdown, to enable this step`;

const getErrorDetails = (t) => ({
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires a <b>Time Entry Table</b>, a <b>Time Tracking Grid</b>,
      {' '}or the <b>{t('Project')} Dropdown</b>
      {' '}plus at least one of Material, Equipment or Costcode Tables or the Sub-Contract Dropdown.
      <br />
      <br />
      Material, Equipment and Costcode Tables and the Sub-Contract Dropdown always need
      {' '}the <b>{t('Project')} Dropdown</b>, even alongside a Time Entry Table or Time Tracking Grid.
      <br />
      <br />
      For the Sub-Contract Dropdown,
      {' '}you must also configure the Description and Contract Amount mappings.
    </div>
  ),
});

const changeOrderFields = [
  { key: 'description', text: 'Description', types: ['text'] },
  { key: 'contractAmount', text: 'Contract Amount', types: ['text'] },
];

const requiredFields = new Set(['description', 'contractAmount']);

const GRID_DEFAULT_CATEGORY_IDS = [LABOR_CATEGORY_ID, EQUIPMENT_CATEGORY_ID];

export default function WorkflowUpdateCostNode({
  isDisplay,
  setElements,
  setDataMap,
  sections = [],
  name,
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const { t } = useTranslation();
    const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
    const HOVER_TEXT = getHoverText(t);
    const DISABLED_TEXT = getDisabledText(t);
    const ERROR_DETAILS = getErrorDetails(t);

    const {
      projectFieldId: initialProjectFieldId,
      materialTables: initialMaterialTables,
      equipmentTables: initialEquipmentTables,
      costcodeTables: initialCostcodeTables,
      timeEntryTables: initialTimeEntryTables,
      timeTrackingGrids: initialTimeTrackingGrids,
      timeTrackingGridCategoryId: initialTimeTrackingGridCategoryId,
      updateBudget: initialUpdateBudget = false,
      subContractFieldId: initialSubContractFieldId,
      fieldMappings: initialFieldMappings = {},
    } = data;

    const title = `Update Cost${disabled ? ' - DISABLED' : ''}`;

    const [selectedProjectFieldId, setSelectedProjectFieldId] = useState(initialProjectFieldId);
    const [selectedMaterialTables, setSelectedMaterialTables] = useState(initialMaterialTables);
    const [selectedEquipmentTables, setSelectedEquipmentTables] = useState(initialEquipmentTables);
    const [selectedCostcodeTables, setSelectedCostcodeTables] = useState(initialCostcodeTables);
    const [selectedTimeEntryTables, setSelectedTimeEntryTables] = useState(initialTimeEntryTables);
    const [selectedTimeTrackingGrids, setSelectedTimeTrackingGrids] = useState(initialTimeTrackingGrids); // eslint-disable-line max-len
    const [selectedGridCategoryId, setSelectedGridCategoryId] = useState(initialTimeTrackingGridCategoryId); // eslint-disable-line max-len
    const [selectedSubContractFieldId, setSelectedSubContractFieldId] = useState(initialSubContractFieldId); // eslint-disable-line max-len
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [shouldUpdateBudget, setShouldUpdateBudget] = useState(initialUpdateBudget);

    const hasTimeTrackingGrid = Array.isArray(selectedTimeTrackingGrids)
      && selectedTimeTrackingGrids.length > 0;

    const disableNext = useMemo(() => {
      const hasTimeEntryTable = Array.isArray(selectedTimeEntryTables)
        && selectedTimeEntryTables.length > 0;

      const hasProjectTable = (
        (Array.isArray(selectedMaterialTables) && selectedMaterialTables.length > 0)
        || (Array.isArray(selectedEquipmentTables) && selectedEquipmentTables.length > 0)
        || (Array.isArray(selectedCostcodeTables) && selectedCostcodeTables.length > 0)
      );

      const hasCorrectSubcontractConfiguration = (
        selectedSubContractFieldId && fieldMappings?.description && fieldMappings?.contractAmount
      );

      // these sources have no project of their own, so they always need the dropdown
      if ((hasProjectTable || selectedSubContractFieldId) && !selectedProjectFieldId) return true;

      const hasProjectSource = selectedProjectFieldId
        && (hasProjectTable || hasCorrectSubcontractConfiguration);

      return !hasTimeEntryTable && !hasTimeTrackingGrid && !hasProjectSource;
    }, [
      selectedProjectFieldId,
      selectedMaterialTables,
      selectedEquipmentTables,
      selectedCostcodeTables,
      selectedTimeEntryTables,
      hasTimeTrackingGrid,
      selectedSubContractFieldId,
      fieldMappings,
    ]);

    const gridCategoryOptions = useMemo(() => {
      const defaultOptions = GRID_DEFAULT_CATEGORY_IDS.map((categoryId) => ({
        value: categoryId,
        label: ID_TO_CATEGORY_MAP[categoryId],
      }));

      const customOptions = Object.values(categoriesIdMap ?? {})
        .filter((category) => typeof category.id === 'string' && category.allowTimeTracking)
        .map((category) => ({ value: category.id, label: category.name }));
      return [...defaultOptions, ...customOptions];
    }, [categoriesIdMap]);

    const gridCategoryValue = useMemo(() => {
      const categoryId = selectedGridCategoryId ?? EQUIPMENT_CATEGORY_ID;
      return gridCategoryOptions.some(({ value }) => value === categoryId)
        ? categoryId
        : EQUIPMENT_CATEGORY_ID;
    }, [selectedGridCategoryId, gridCategoryOptions]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const {
      isToggled: isMappingDrawerVisible,
      toggle: toggleMappingDrawer,
    } = useToggle();

    const setMap = {
      projectFieldId: setSelectedProjectFieldId,
      materialTables: setSelectedMaterialTables,
      equipmentTables: setSelectedEquipmentTables,
      costcodeTables: setSelectedCostcodeTables,
      timeEntryTables: setSelectedTimeEntryTables,
      timeTrackingGrids: setSelectedTimeTrackingGrids,
      timeTrackingGridCategoryId: setSelectedGridCategoryId,
      subContractFieldId: setSelectedSubContractFieldId,
      fieldMappings: setFieldMappings,
    };

    const onDataChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      setMap[key]?.(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
    }, [id, setDataMap]);

    const onCheckChange = useCallback((key) => (checked) => {
      if (key === 'updateBudget') setShouldUpdateBudget(checked);
      onDataChange(key)(checked);
    }, [onDataChange]);

    const {
      materialTableFields,
      equipmentTableFields,
      costcodeTableFields,
      timeEntryTableFields,
      timeTrackingGridFields,
    } = useMemo(() => (
      sections.reduce((acc, section) => {
        const { fields = [] } = section;
        fields.forEach((field) => {
          const { id: fieldId, configProps: { title: fieldTitle } = {} } = field;
          const type = getType(field);
          const fieldObj = { label: fieldTitle, value: fieldId };
          if (type === 'table - Materials') {
            acc.materialTableFields.push(fieldObj);
          } else if (type === 'table - Equipment') {
            acc.equipmentTableFields.push(fieldObj);
          } else if (type === 'table - CostCodes') {
            acc.costcodeTableFields.push(fieldObj);
          } else if (type === 'table - TimeEntry') {
            acc.timeEntryTableFields.push(fieldObj);
          } else if (type === 'timeTrackingGrid') {
            acc.timeTrackingGridFields.push(fieldObj);
          }
        });
        return acc;
      }, {
        materialTableFields: [],
        equipmentTableFields: [],
        costcodeTableFields: [],
        timeEntryTableFields: [],
        timeTrackingGridFields: [],
      })
    ), [sections]);

    const { projectDropdownFields, subContractDropdownFields } = useMemo(() => (
      sections.reduce((acc, section) => {
        const { fields = [] } = section;
        fields.forEach((field) => {
          const { id: fieldId, configProps: { title: fieldTitle } = {} } = field;
          const type = getType(field);
          if (type === 'dropdown - Projects') {
            acc.projectDropdownFields.push({ label: fieldTitle, value: fieldId });
          } else if (type === 'dropdown - PayableSubContracts') {
            acc.subContractDropdownFields.push({ label: fieldTitle, value: fieldId });
          }
        });
        return acc;
      }, { projectDropdownFields: [], subContractDropdownFields: [] })
    ), [sections]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={DollarCircleOutlined}
        type="updateCost"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : { width: 210 }}
        errorPopover={disableNext ? ERROR_DETAILS : null}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              <Col style={{ paddingRight: 5 }} span={20}>
                Select
                {' '}
                {t('Project')}
                {' '}
                Dropdown:
              </Col>
              <Col>
                <HoverHelp
                  content={(
                    <div style={{ width: 300 }}>
                      Material, Equipment and Costcode Table costs and Sub-Contract change orders
                      {' '}go to this {t('Project').toLowerCase()}.
                      {' '}Optional when only Time Entry Tables or Time Tracking Grids are selected.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('projectFieldId')}
                value={selectedProjectFieldId}
                showSearch
                allowClear
                optionFilterProp="label"
                options={projectDropdownFields}
              />
            </Row>
            <Divider style={{ marginTop: 0, marginBottom: 0 }} />
            <OnTraccrCheckbox
              style={{ marginTop: 10 }}
              onChange={onCheckChange('updateBudget')}
              defaultChecked={initialUpdateBudget}
              value={shouldUpdateBudget}
              label="Update Budget"
              hoverNode={(
                <div style={{ width: 300 }}>
                  If turned on, table cost updates will update the
                  {' '}{t('Project').toLowerCase()} budget page.
                  <br />
                  <br />
                  If turned off, table cost updates will update the
                  {' '}{t('Project').toLowerCase()} progress page.
                </div>
              )}
            />
            <Row style={{ marginTop: 10 }}>
              Select Material Table Field(s):
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                mode="multiple"
                onChange={onDataChange('materialTables')}
                value={selectedMaterialTables}
                showSearch
                optionFilterProp="label"
                options={materialTableFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select Equipment Table Field(s):
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                mode="multiple"
                onChange={onDataChange('equipmentTables')}
                value={selectedEquipmentTables}
                showSearch
                optionFilterProp="label"
                options={equipmentTableFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Select Costcode Table Field(s):
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                mode="multiple"
                onChange={onDataChange('costcodeTables')}
                value={selectedCostcodeTables}
                showSearch
                optionFilterProp="label"
                options={costcodeTableFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              <Col style={{ paddingRight: 5 }} span={20}>
                Select Time Entry Table Field(s):
              </Col>
              <Col>
                <HoverHelp
                  content={(
                    <div style={{ width: 300 }}>
                      Any material, equipment & cost code table custom fields in a time entry
                      table update the budget/progress of that time entry&apos;s
                      {' '}{t('Project').toLowerCase()}.
                      {' '}Time entries without a {t('Project').toLowerCase()} are skipped.
                      <br /><br />
                      Rows with a cost code selected are logged under that cost code.
                      Rows without one inherit the cost code of the time entry.
                      If the time entry has no cost code either, material and equipment
                      rows are logged as uncategorized and cost code rows are skipped.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                mode="multiple"
                onChange={onDataChange('timeEntryTables')}
                value={selectedTimeEntryTables}
                showSearch
                optionFilterProp="label"
                options={timeEntryTableFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              <Col style={{ paddingRight: 5 }} span={20}>
                Select Time Tracking Grid Field(s):
              </Col>
              <Col>
                <HoverHelp
                  content={(
                    <div style={{ width: 300 }}>
                      Equipment rows are costed at each equipment&apos;s hourly cost
                      {' '}on each column&apos;s {t('Project').toLowerCase()}.
                      {' '}User rows are not costed by this step.
                      <br /><br />
                      Columns on a global cost code or with no {t('Project').toLowerCase()} are
                      skipped.
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                mode="multiple"
                onChange={onDataChange('timeTrackingGrids')}
                value={selectedTimeTrackingGrids}
                showSearch
                optionFilterProp="label"
                options={timeTrackingGridFields}
              />
            </Row>
            {hasTimeTrackingGrid && (
              <>
                <Row style={{ marginTop: 10 }}>
                  <Col style={{ paddingRight: 5 }} span={20}>
                    Cost Category:
                  </Col>
                  <Col>
                    <HoverHelp
                      content={(
                        <div style={{ width: 300 }}>
                          Grid equipment costs go to this category on each cost code.
                          {' '}It is added to the cost code if missing.
                          <br /><br />
                          A cost code can only have one time tracking category (Labor, Overhead,
                          {' '}or a custom category with time tracking). If the cost code already
                          {' '}has a different one, the cost goes to that category instead.
                        </div>
                      )}
                    />
                  </Col>
                </Row>
                <Row style={{ margin: '10px 0px' }}>
                  <Select
                    onChange={onDataChange('timeTrackingGridCategoryId')}
                    value={gridCategoryValue}
                    showSearch
                    optionFilterProp="label"
                    options={gridCategoryOptions}
                  />
                </Row>
              </>
            )}
            <Divider style={{ marginTop: 0, marginBottom: 0 }} />
            <Row style={{ marginTop: 10 }}>
              Select Sub-Contract Dropdown Field:
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onDataChange('subContractFieldId')}
                value={selectedSubContractFieldId}
                showSearch
                optionFilterProp="label"
                options={subContractDropdownFields}
              />
            </Row>
            {selectedSubContractFieldId && (
              <Row style={{ marginTop: 10 }}>
                <BorderlessButton
                  title="configure"
                  style={{ margin: 5 }}
                  iconNode={<SettingOutlined />}
                  onClick={toggleMappingDrawer}
                />
              </Row>
            )}
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={isMappingDrawerVisible}
              onClose={toggleMappingDrawer}
              onSubmit={toggleMappingDrawer}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onDataChange('fieldMappings')}
              data={data}
              setDataMap={setDataMap}
              fields={changeOrderFields}
              requiredFields={requiredFields}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
